import { QuestionFilters } from "./questionService";
import { GEMINI_API_KEY, GEMINI_API_URL } from "./aiService";

export interface IaFlashcard {
  id: string;
  front: string;
  back: string;
  subject?: string;
  topic?: string;
  source?: "ia";
  difficulty?: "fácil" | "médio" | "difícil";
}

const SYS_PROMPT = `Gere flashcards claros e concisos para concurseiros brasileiros.
Saída obrigatória: JSON array com objetos {"front","back","subject","topic","difficulty"}.
Regras:
- front: 1-2 frases no máximo, sem listas longas, sem alternativas A/B/C/D.
- back: resposta direta + dica/macete curta; cite fundamento legal se existir.
- difficulty: "fácil" | "médio" | "difícil" (escolha com base na densidade do conceito).
- Não invente dados; se não souber, responda com um flashcard genérico de revisão.
- Português do Brasil, tom direto.
- Máx 5 linhas no back. Sem HTML, sem markdown.
`;

function sanitize(text: string, max = 400): string {
  if (!text) return "";
  return text
    .replace(/\s+/g, " ")
    .replace(/\*\*/g, "")
    .replace(/<[^>]+>/g, "")
    .trim()
    .slice(0, max);
}

function isCardValid(card: Partial<IaFlashcard>): card is IaFlashcard {
  return !!card.front && !!card.back;
}

function isLowQualityCard(card: IaFlashcard): boolean {
  const text = `${card.front} ${card.back}`.toLowerCase();
  return [
    "conceito-chave",
    "definição resumida",
    "resumo curto",
    "ponto importante",
    "aplicação prática",
    "leia a lei seca e resolva 5 questões",
    "de tópico",
    "tópico:",
    "de matéria",
    "matéria:",
    "qual a definição objetiva de tópico",
  ].some((p) => text.includes(p));
}

function isGenericLabel(value?: string): boolean {
  const v = String(value || "").trim().toLowerCase();
  return !v || ["tópico", "topico", "matéria", "materia", "geral", "assunto"].includes(v);
}

function dedupe(cards: IaFlashcard[]): IaFlashcard[] {
  const seen = new Set<string>();
  return cards.filter((c) => {
    const key = (c.front || "").toLowerCase().slice(0, 120);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

async function callGemini(subject: string, topic: string, maxCards: number): Promise<IaFlashcard[]> {
  if (!GEMINI_API_URL) return [];

  const userPrompt = `Matéria: ${subject}\nTópico: ${topic}\nQuantidade: ${maxCards}`;
  const url = GEMINI_API_KEY ? `${GEMINI_API_URL}?key=${GEMINI_API_KEY}` : GEMINI_API_URL;

  const resp = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      contents: [
        {
          role: "user",
          parts: [
            { text: SYS_PROMPT },
            { text: userPrompt }
          ]
        }
      ],
      generationConfig: {
        temperature: 0.35,
        maxOutputTokens: 800,
        responseMimeType: "application/json"
      }
    })
  });

  if (!resp.ok) return [];
  const data = await resp.json();
  const raw = data?.candidates?.[0]?.content?.parts?.[0]?.text || "";
  let jsonString = raw.replace(/```json/gi, "").replace(/```/g, "").trim();

  // tenta encontrar array
  const start = jsonString.indexOf("[");
  const end = jsonString.lastIndexOf("]");
  if (start === -1 || end === -1 || end <= start) return [];
  jsonString = jsonString.slice(start, end + 1);

  let parsed: any[] = [];
  try {
    parsed = JSON.parse(jsonString);
  } catch (e) {
    return [];
  }

  const cleaned = parsed
    .map((c, idx) => {
      const card: IaFlashcard = {
        id: c.id || `ia_${idx}_${Date.now()}`,
        front: sanitize(String(c.front || ""), 240),
        back: sanitize(String(c.back || ""), 360),
        subject: sanitize(String(c.subject || subject), 120),
        topic: sanitize(String(c.topic || topic), 180),
        difficulty: c.difficulty === "difícil" || c.difficulty === "médio" ? c.difficulty : "fácil",
        source: "ia",
      };
      return card;
    })
    .filter(isCardValid)
    .filter((c) => c.front.length >= 20 && c.back.length >= 10)
    .filter((c) => !isLowQualityCard(c));

  return dedupe(cleaned).slice(0, maxCards);
}

// Gera flashcards por IA; fallback para mocks simples se falhar
export async function generateFlashcardsFromTopic(
  filters?: QuestionFilters,
  maxCards: number = 10,
): Promise<IaFlashcard[]> {
  const subject = filters?.subject || "Matéria";
  const topic = filters?.topic || "Tópico";
  const safeSubject = isGenericLabel(subject) ? "Conteúdo cobrado" : subject;
  const safeTopic = isGenericLabel(topic) ? "ponto central do tema" : topic;

  // tenta até 2 chamadas (caso venha vazio)
  for (let attempt = 0; attempt < 2; attempt++) {
    const cards = await callGemini(subject, topic, maxCards);
    if (cards.length > 0) return cards;
  }

  // fallback seguro (útil, sem placeholder genérico)
  const topicNorm = String(safeTopic || "").toLowerCase();
  const examples = topicNorm.includes("a fim") || topicNorm.includes("afim")
    ? [
        {
          front: `Qual a diferença entre "a fim de" e "afim"?`,
          back: `"A fim de" = locução prepositiva (finalidade: com o objetivo de). "Afim" = adjetivo (semelhante, relacionado). Troque por "para"; se funcionar, geralmente é "a fim de".`,
        },
        {
          front: `Na frase "Eles estavam afins", o termo correto é "afim" ou "a fim"?`,
          back: `Use "afins" (adjetivo no plural), pois indica afinidade/semelhança. "A fim" separado costuma indicar intenção: "estou a fim de estudar".`,
        },
        {
          front: `Macete de prova para não errar "a fim" x "afim"`,
          back: `Se der para substituir por "para", use "a fim de". Se a ideia for "semelhante/compatível", use "afim". Bancas cobram muito essa troca de classe gramatical.`,
        },
      ]
    : [
        {
          front: `${safeSubject}: qual a definição objetiva de ${safeTopic}?`,
          back: `Defina ${safeTopic} em 1 frase e traga 1 exemplo prático de prova para fixação.`,
        },
        {
          front: `${safeSubject}: qual a pegadinha mais comum em ${safeTopic}?`,
          back: `Pegadinha recorrente: confundir regra geral com exceção. Revise quando a regra NÃO se aplica e compare com um caso típico de banca.`,
        },
        {
          front: `${safeSubject}: como ${safeTopic} costuma aparecer em questões?`,
          back: `Aparece em enunciados curtos com troca de termo-chave. Foque em palavras de condição e exceção (exceto, salvo, somente se).`,
        },
      ];
  return examples
    .slice(0, maxCards)
    .map((c, idx) => ({
      id: `ia_${idx}_${Date.now()}`,
      front: sanitize(c.front, 240),
      back: sanitize(c.back, 360),
      subject: safeSubject,
      topic: safeTopic,
      source: "ia" as const,
      difficulty: "fácil" as const,
    }))
    .filter((c) => !isLowQualityCard(c));
}
